import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useCollectionBinding,
  useSlotGroup,
  usePropertyAddressUrl,
  SectionContextProvider,
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { clean, hoursRows, stagger, telHref, type OfficeHour } from '../utils/quinn';
import { useRawProperty } from '../utils/hooks';
import { tourCta, useSiteFlags } from '../utils/site';

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'Beside the form. Without a self-guided tour URL on the property it is hidden (the form is already the contact).',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const infoRow =
  'flex min-w-0 flex-col gap-1 [container-type:inline-size] [&_span]:text-[13px] [&_span]:font-medium [&_span]:leading-[18px] [&_span]:text-[--q-muted]';
const infoLink =
  'self-start whitespace-nowrap text-base font-medium leading-6 text-[--q-ink] no-underline hover:underline hover:underline-offset-[3px]';

/** Asterisk in the top-right corner of every required field of the lead form block (it only has placeholders, no labels). */
const requiredMarks =
  "[&_div:has(>[required])]:relative [&_div:has(>[required])]:after:pointer-events-none [&_div:has(>[required])]:after:absolute [&_div:has(>[required])]:after:right-3 [&_div:has(>[required])]:after:top-1.5 [&_div:has(>[required])]:after:text-base [&_div:has(>[required])]:after:font-semibold [&_div:has(>[required])]:after:leading-none [&_div:has(>[required])]:after:text-[--q-primary-on-paper] [&_div:has(>[required])]:after:content-['*']";

const WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

/** Contact Us (Paper 12): the lead form in a card, the leasing office beside it, then the map. */
export function ContactDetails() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const directions = usePropertyAddressUrl();
  const tour = useSlotGroup('contactdetails.tour_cta', tourCtaDef);
  const hours = useCollectionBinding<OfficeHour>('contactdetails.hours', { collection: 'office_hours' });
  const form = useBlockSlot('contactdetails.form_block', { defaultValue: 'multihub-lead-form' });

  const tourLink = tourCta(tour, flags.selfTourUrl);
  const tel = telHref(flags.phone);
  const street = clean(p.property_address_1);
  const city = [[clean(p.property_address_city), clean(p.property_address_state)].filter(Boolean).join(', '), clean(p.property_address_zip)].filter(Boolean).join(' ');
  const address = [street, city].filter(Boolean).join(', ');
  const rows = hoursRows([...hours].sort((a, b) => WEEK.indexOf(a.day || '') - WEEK.indexOf(b.day || '')), false);
  const mapSrc = address ? `https://maps.google.com/maps?q=${encodeURIComponent(`${clean(p.property_name) || ''} ${address}`.trim())}&z=15&output=embed` : null;

  return (
    <section
      className="section [&.section]:pb-4xl [&.section]:pt-20 max-[767px]:[&.section]:pb-14 max-[767px]:[&.section]:pt-10"
      id="contact-form"
    >
      <div className="flex items-start justify-between gap-16 max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-12">
        <div
          className="flex min-w-0 flex-col gap-5 rounded-lg border border-[--q-border] bg-[--q-paper] p-9 [flex:2_1_0%] max-[1199px]:basis-auto max-[767px]:px-5 max-[767px]:py-6"
          data-reveal
        >
          <div className="flex flex-col gap-1.5">
            <RichTextContent path="contactdetails.eyebrow" as="div" className="eyebrow" />
            <RichTextContent
              path="contactdetails.title"
              as="h2"
              className="m-0 font-heading text-[36px] font-light leading-[42px] tracking-[--q-tracking-tight] [&_p]:m-0 max-[767px]:text-[28px] max-[767px]:leading-[34px]"
            />
            <RichTextContent path="contactdetails.sub" as="p" className="m-0 text-sm leading-5 text-[--q-muted] [&_p]:m-0" />
          </div>
          <div className="-mb-2 flex items-baseline gap-1 text-[13px] leading-[18px] text-[--q-muted]">
            <span className="text-base font-semibold leading-none text-[--q-primary-on-paper]">*</span>
            <RichTextContent path="contactdetails.required_note" as="div" className="[&_p]:m-0" />
          </div>
          <EditableToken path="contactdetails.form_block" type="block" as="div" className={`contactus__form ${requiredMarks}`}>
            {form ? (
              <SectionContextProvider sectionId={form.sectionId}>
                <form.BlockComponent sectionId={form.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="flex min-h-[240px] items-center justify-center rounded-md bg-[--q-surface] text-[--q-muted]">Contact form</div>
            )}
          </EditableToken>
          <EditableSlotGroup groupPath="contactdetails.tour_cta" definition={tourCtaDef}>
            {tourLink.isTour && (
              <div className="flex justify-end">
                <a className="btn btn-secondary" href={tourLink.href} target={tour._target || '_self'} data-vibesite-event={tour.vibesite_event || undefined}>
                  {tourLink.label}
                </a>
              </div>
            )}
          </EditableSlotGroup>
        </div>

        <div
          className="flex min-w-0 flex-col gap-9 pt-3 [flex:1_1_0%] max-[1199px]:basis-auto max-[767px]:gap-7 max-[767px]:pt-0"
          data-reveal
          style={stagger(1)}
        >
          <div className="flex flex-col gap-4">
            <RichTextContent path="contactdetails.office_label" as="div" className="label [&_p]:m-0" />
            {street && (
              <div className="-mb-3 font-heading text-[28px] leading-[34px] tracking-[--q-tracking-tight] max-[767px]:text-[24px] max-[767px]:leading-[30px]">{street}</div>
            )}
            {city && <div className="text-base leading-[26px] text-[--q-muted]">{city}</div>}
            {address && (
              <a
                className="textlink self-start [&.textlink]:inline-flex [&.textlink]:min-h-6 [&.textlink]:items-center [&.textlink]:text-[15px] max-[767px]:[&.textlink]:min-h-11"
                href={directions}
                target="_blank"
                rel="noopener"
              >
                Get Directions
              </a>
            )}
          </div>
          {tel && (
            <div className="flex flex-col gap-3.5 border-t border-t-[--q-border] pt-6">
              <div className={infoRow}>
                <span>Phone</span>
                <a className={`dniphone dniphonehref ${infoLink}`} href={tel}>{flags.phone}</a>
              </div>
            </div>
          )}
          <EditableCollectionBinding
            bindingPath="contactdetails.hours"
            label="Office Hours"
            className="flex flex-col gap-3 border-t border-t-[--q-border] pt-6 empty:hidden"
          >
            {rows.length > 0 && (
              <>
                <RichTextContent path="contactdetails.hours_label" as="div" className="label [&_p]:m-0" />
                {rows.map(([d, t]) => (
                  <div className="flex justify-between gap-4" key={d}>
                    <span className="font-heading text-[22px] leading-[30px]">{d}</span>
                    <span className="text-base leading-[30px] text-[--q-muted]">{t}</span>
                  </div>
                ))}
              </>
            )}
          </EditableCollectionBinding>
        </div>
      </div>

      {mapSrc && (
        <div className="mt-[72px] h-[420px] overflow-hidden rounded-lg bg-[--q-surface] max-[767px]:mt-10 max-[767px]:h-[300px]" data-reveal>
          <iframe className="block h-full w-full [border:0]" src={mapSrc} title={`Map to ${address}`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactDetails',
  displayName: 'Contact Details',
  tokens: {
    'contactdetails.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Send a note</p>' },
    'contactdetails.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Send the leasing team a note</p>' },
    'contactdetails.sub': { label: 'Line Under the Title', type: 'richtext', defaultValue: '<p>We reply within one business day.</p>' },
    'contactdetails.required_note': { label: 'Required Fields Note', type: 'richtext', defaultValue: '<p>required field</p>' },
    'contactdetails.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactdetails.office_label': { label: 'Office Label', type: 'richtext', defaultValue: '<p>Leasing office</p>' },
    'contactdetails.hours_label': { label: 'Hours Label', type: 'richtext', defaultValue: '<p>Hours</p>' },
  },
  slotGroups: {
    'contactdetails.tour_cta': tourCtaDef,
  },
  collectionBindings: {
    'contactdetails.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactDetails as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactDetails;
